import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../config";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import {
  TrendingUp, Search, Eye, Shield, Globe, Loader2, CheckCircle, X,
  AlertTriangle, ArrowUp, ArrowDown, Minus, Plus, Trash2, RefreshCw, Zap
} from "lucide-react";
import { OrderNowBottomCTA } from "../components/common/ServiceOrderBundle";
import { SignInGate } from "./CompetitorSpyPage";

const ENGINE_META = {
  chatgpt: { name: "ChatGPT", color: "#10A37F", bg: "bg-emerald-900/20" },
  perplexity: { name: "Perplexity", color: "#20808D", bg: "bg-teal-900/20" },
  gemini: { name: "Gemini", color: "#4285F4", bg: "bg-blue-900/20" },
  copilot: { name: "Copilot", color: "#7C3AED", bg: "bg-purple-900/20" },
};

export default function BrandMonitorPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [monitors, setMonitors] = useState([]);
  const [selectedMonitor, setSelectedMonitor] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [form, setForm] = useState({ brand_name: "", website_url: "", industry: "", location: "", competitors: "" });

  useEffect(() => {
    if (!user) return;
    fetchMonitors();
  }, [user]);

  const fetchMonitors = async () => {
    if (!user) return;
    try {
      const r = await axios.get(`${API}/brand-monitor/status?email=${encodeURIComponent(user.email)}`);
      setMonitors(r.data.monitors || []);
      if (r.data.monitors?.length > 0 && !selectedMonitor) {
        setSelectedMonitor(r.data.monitors[0]);
        fetchResults(r.data.monitors[0].monitor_id);
      }
    } catch {}
  };

  const fetchResults = async (monitorId) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/brand-monitor/results/${monitorId}`);
      setResults(r.data);
    } catch {}
    setLoading(false);
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    if (!form.brand_name || !form.website_url) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/brand-monitor/setup`, {
        ...form,
        competitors: form.competitors.split(",").map(c => c.trim()).filter(Boolean),
        email: user.email,
        language: language || "en",
      });
      setShowSetup(false);
      setForm({ brand_name: "", website_url: "", industry: "", location: "", competitors: "" });
      await fetchMonitors();
      setSelectedMonitor(r.data);
      // Auto-run first check
      await runCheck(r.data.monitor_id);
    } catch {}
    setLoading(false);
  };

  const runCheck = async (monitorId) => {
    setChecking(true);
    try {
      const r = await axios.post(`${API}/brand-monitor/check`, { monitor_id: monitorId, language: language || "en" });
      setResults(prev => prev ? { ...prev, results: [r.data, ...(prev.results || [])] } : { results: [r.data] });
      await fetchMonitors();
    } catch {}
    setChecking(false);
  };

  const handleDelete = async (monitorId) => {
    try {
      await axios.delete(`${API}/brand-monitor/${monitorId}`);
      setMonitors(prev => prev.filter(m => m.monitor_id !== monitorId));
      if (selectedMonitor?.monitor_id === monitorId) {
        setSelectedMonitor(null);
        setResults(null);
      }
    } catch {}
  };

  const latestResult = results?.results?.[0];
  const TrendIcon = ({ trend }) => {
    if (trend === "up") return <ArrowUp className="w-4 h-4 text-emerald-400" />;
    if (trend === "down") return <ArrowDown className="w-4 h-4 text-red-400" />;
    return <Minus className="w-4 h-4 text-[var(--color-text-muted)]" />;
  };

  const finalCta = (
    <OrderNowBottomCTA
      packageId="addon_brand_monitor"
      priceEur={19}
      interval="mes"
      serviceName="Brand Monitor"
      headline="Sleduj svoju značku v AI vyhľadávačoch"
      subcopy="Alerty vždy keď ChatGPT, Perplexity alebo Gemini spomenú tvoju firmu. Weekly brand pulse report v inbox-e."
      testid="brand-monitor-final-cta"
    />
  );

  if (!user) return (
    <SignInGate icon={Eye} title={t.bm_title || "AI Brand Monitoring"} sub={t.bm_sign_in_desc || "Sign in to monitor your brand across AI search engines."}>
      {finalCta}
    </SignInGate>
  );

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-white">
      {/* Header */}
      <header className="bg-[var(--color-bg)]/60 backdrop-blur-xl border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#a855f7] flex items-center justify-center"><TrendingUp className="w-5 h-5 text-white" /></div>
            <span className="text-xl font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
          </a>
          <div className="flex items-center gap-4">
            <a href="/dashboard" className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] font-['JetBrains_Mono'] uppercase">{t.nav_dashboard || "Dashboard"}</a>
            <a href="/" className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] font-['JetBrains_Mono'] uppercase">{t.nav_home || "Home"}</a>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Title */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Eye className="w-6 h-6 text-[#a855f7]" />
              <h1 className="text-3xl font-black font-['Cabinet_Grotesk'] tracking-tight" data-testid="brand-monitor-title">{t.bm_title || "AI Brand Monitoring"}</h1>
              <span className="px-2 py-0.5 bg-[#FFE600]/10 text-[#FFE600] text-xs font-bold">€19/mo</span>
            </div>
            <p className="text-[var(--color-text-muted)]">{t.bm_desc || "Track if ChatGPT, Perplexity, Gemini & Copilot cite your brand"}</p>
          </div>
          <button onClick={() => setShowSetup(true)} className="flex items-center gap-2 px-6 py-3 bg-[#a855f7] text-white font-bold uppercase text-sm hover:bg-purple-600 transition-colors" data-testid="add-monitor-btn">
            <Plus className="w-4 h-4" /> {t.bm_add || "Add Monitor"}
          </button>
        </div>

        {/* Setup Modal */}
        {showSetup && (
          <div className="fixed inset-0 bg-[var(--color-bg)]/80 z-50 flex items-center justify-center p-4" data-testid="setup-modal">
            <form onSubmit={handleSetup} className="bg-[var(--color-surface)] border border-[var(--color-border)] p-8 max-w-lg w-full">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-black font-['Cabinet_Grotesk']">{t.bm_setup || "Setup Brand Monitor"}</h2>
                <button type="button" onClick={() => setShowSetup(false)}><X className="w-5 h-5 text-[var(--color-text-muted)]" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-[var(--color-text-muted)] mb-1">{t.bm_brand_name || "Brand Name"} *</label>
                  <input value={form.brand_name} onChange={e => setForm(p => ({...p, brand_name: e.target.value}))} placeholder="SuperStavebniny" required className="w-full px-4 py-3 bg-[var(--color-bg)] border border-[var(--color-border)] text-white focus:border-[#a855f7] focus:outline-none" data-testid="setup-brand-name" />
                </div>
                <div>
                  <label className="block text-sm text-[var(--color-text-muted)] mb-1">{t.bm_website || "Website URL"} *</label>
                  <input value={form.website_url} onChange={e => setForm(p => ({...p, website_url: e.target.value}))} placeholder="https://superstavebniny.sk" required className="w-full px-4 py-3 bg-[var(--color-bg)] border border-[var(--color-border)] text-white focus:border-[#a855f7] focus:outline-none" data-testid="setup-website-url" />
                </div>
                <div>
                  <label className="block text-sm text-[var(--color-text-muted)] mb-1">{t.bm_industry || "Industry"}</label>
                  <input value={form.industry} onChange={e => setForm(p => ({...p, industry: e.target.value}))} placeholder="stavebné materiály" className="w-full px-4 py-3 bg-[var(--color-bg)] border border-[var(--color-border)] text-white focus:border-[#a855f7] focus:outline-none" data-testid="setup-industry" />
                </div>
                <div>
                  <label className="block text-sm text-[var(--color-text-muted)] mb-1">{t.bm_location || "Location"}</label>
                  <input value={form.location} onChange={e => setForm(p => ({...p, location: e.target.value}))} placeholder="Bratislava, Slovensko" className="w-full px-4 py-3 bg-[var(--color-bg)] border border-[var(--color-border)] text-white focus:border-[#a855f7] focus:outline-none" data-testid="setup-location" />
                </div>
                <div>
                  <label className="block text-sm text-[var(--color-text-muted)] mb-1">{t.bm_competitors || "Competitors"} ({t.bm_comma || "comma separated"})</label>
                  <input value={form.competitors} onChange={e => setForm(p => ({...p, competitors: e.target.value}))} placeholder="Stavmat, Woodcote, Baumax" className="w-full px-4 py-3 bg-[var(--color-bg)] border border-[var(--color-border)] text-white focus:border-[#a855f7] focus:outline-none" data-testid="setup-competitors" />
                </div>
              </div>
              <button type="submit" disabled={loading} className="w-full mt-6 py-3 bg-[#a855f7] text-white font-bold uppercase hover:bg-purple-600 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="setup-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                {t.bm_start || "Start Monitoring"}
              </button>
            </form>
          </div>
        )}

        {/* Monitor Selector */}
        {monitors.length > 0 && (
          <div className="flex gap-3 mb-8 overflow-x-auto pb-2">
            {monitors.map(m => (
              <button key={m.monitor_id} onClick={() => { setSelectedMonitor(m); fetchResults(m.monitor_id); }}
                className={`flex items-center gap-3 px-5 py-3 border whitespace-nowrap transition-all ${
                  selectedMonitor?.monitor_id === m.monitor_id ? 'bg-[var(--color-surface)] border-[#a855f7] text-white' : 'bg-[var(--color-bg)] border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-primary)]'
                }`} data-testid={`monitor-${m.monitor_id}`}>
                <Globe className="w-4 h-4" />
                <span className="font-bold font-['Cabinet_Grotesk']">{m.brand_name}</span>
                {m.latest_visibility > 0 && (
                  <span className={`text-sm font-mono font-bold ${m.latest_visibility >= 60 ? 'text-emerald-400' : m.latest_visibility >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                    {m.latest_visibility}%
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* No monitors */}
        {monitors.length === 0 && !showSetup && (
          <div className="text-center py-20">
            <Eye className="w-16 h-16 text-zinc-700 mx-auto mb-6" />
            <h2 className="text-2xl font-black font-['Cabinet_Grotesk'] mb-3">{t.bm_no_monitors || "No Brands Monitored Yet"}</h2>
            <p className="text-[var(--color-text-muted)] mb-8 max-w-md mx-auto">{t.bm_no_monitors_desc || "Add your brand to start tracking how AI search engines see you."}</p>
            <button onClick={() => setShowSetup(true)} className="px-8 py-4 bg-[#a855f7] text-white font-bold uppercase text-lg hover:bg-purple-600" data-testid="first-monitor-btn">
              <Plus className="w-5 h-5 inline mr-2" /> {t.bm_add_brand || "Add Your Brand"}
            </button>
          </div>
        )}

        {/* Results */}
        {selectedMonitor && latestResult && (
          <div className="space-y-6" data-testid="brand-results">
            {/* Actions bar */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-black font-['Cabinet_Grotesk']">{selectedMonitor.brand_name}</h2>
                <TrendIcon trend={latestResult.trend} />
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => runCheck(selectedMonitor.monitor_id)} disabled={checking}
                  className="flex items-center gap-2 px-4 py-2 bg-[var(--color-surface)] border border-[var(--color-border)] text-sm font-bold hover:border-zinc-500 disabled:opacity-50" data-testid="recheck-btn">
                  {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  {t.bm_recheck || "Re-check"}
                </button>
                <button onClick={() => handleDelete(selectedMonitor.monitor_id)}
                  className="p-2 text-[var(--color-text-muted)] hover:text-red-500" data-testid="delete-monitor-btn">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Overall Score */}
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-8 text-center" data-testid="overall-visibility">
              <p className="text-[var(--color-text-muted)] text-sm font-['JetBrains_Mono'] uppercase tracking-wider mb-2">{t.bm_overall || "Overall AI Visibility"}</p>
              <p className={`text-7xl font-black font-['JetBrains_Mono'] ${
                latestResult.overall_visibility >= 70 ? 'text-emerald-400' :
                latestResult.overall_visibility >= 40 ? 'text-[#FFE600]' : 'text-[#a855f7]'
              }`}>{latestResult.overall_visibility}%</p>
              <div className="flex items-center justify-center gap-2 mt-2">
                <TrendIcon trend={latestResult.trend} />
                <span className="text-sm text-[var(--color-text-muted)] capitalize">{latestResult.trend}</span>
              </div>
            </div>

            {/* Engine Cards */}
            <div className="grid md:grid-cols-4 gap-4" data-testid="engine-cards">
              {(latestResult.engines || []).map((eng, i) => {
                const meta = ENGINE_META[eng.id] || { name: eng.id, color: "#666", bg: "bg-[var(--color-surface)]/20" };
                return (
                  <div key={i} className={`bg-[var(--color-surface)] border border-[var(--color-border)] p-6 hover:border-[var(--color-primary)] transition-colors`} data-testid={`engine-${eng.id}`}>
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-bold font-['Cabinet_Grotesk']" style={{color: meta.color}}>{meta.name}</span>
                      {eng.mentioned ? (
                        <CheckCircle className="w-5 h-5 text-emerald-400" />
                      ) : (
                        <X className="w-5 h-5 text-red-400" />
                      )}
                    </div>
                    <p className={`text-4xl font-black font-['JetBrains_Mono'] mb-1 ${eng.citation_score >= 60 ? 'text-emerald-400' : eng.citation_score >= 40 ? 'text-[#FFE600]' : 'text-[var(--color-text-muted)]'}`}>
                      {eng.citation_score}
                    </p>
                    <p className="text-xs text-[var(--color-text-muted)]">{t.bm_citation_score || "Citation Score"}</p>
                    {eng.mentioned ? (
                      <p className="text-xs text-emerald-400/70 mt-3 leading-relaxed">{eng.context}</p>
                    ) : (
                      <p className="text-xs text-red-400/70 mt-3">
                        {eng.competitor_mentioned ? `${t.bm_recommends || "Recommends"}: ${eng.competitor_mentioned}` : (t.bm_not_cited || "Not cited")}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Detailed Recommendations */}
            {latestResult.recommendations?.length > 0 ? (
              <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6" data-testid="recommendations">
                <h3 className="text-sm font-bold text-[#FFE600] uppercase tracking-wider mb-5 font-['JetBrains_Mono'] flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  {t.bm_recommendations || "How to increase your visibility"}
                </h3>
                <div className="space-y-4">
                  {latestResult.recommendations.map((rec, i) => {
                    const impactColors = {
                      high: "bg-red-900/20 text-red-400 border-purple-900/30",
                      medium: "bg-amber-900/20 text-amber-400 border-amber-900/30",
                      low: "bg-emerald-900/20 text-emerald-400 border-emerald-900/30",
                    };
                    const catIcons = {
                      content: "pencil", technical: "code", backlinks: "link",
                      schema: "database", ai_optimization: "cpu",
                    };
                    return (
                      <div key={i} className="flex items-start gap-4 p-4 bg-[var(--color-bg)] border border-[var(--color-border)] hover:border-[var(--color-border)] transition-colors" data-testid={`rec-${i}`}>
                        <div className="w-8 h-8 bg-[#FFE600]/10 flex items-center justify-center flex-shrink-0 text-[#FFE600] font-black font-['Cabinet_Grotesk'] text-sm">
                          {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h4 className="text-white font-bold font-['Cabinet_Grotesk'] text-sm">{rec.title}</h4>
                            <span className={`px-2 py-0.5 text-[9px] font-bold uppercase border ${impactColors[rec.impact] || impactColors.medium}`}>
                              {rec.impact === "high" ? (t.bm_high || "High") : rec.impact === "medium" ? (t.bm_medium || "Medium") : (t.bm_low || "Low")}
                            </span>
                          </div>
                          <p className="text-[var(--color-text-muted)] text-xs leading-relaxed">{rec.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : latestResult.top_recommendation ? (
              <div className="bg-[#FFE600]/5 border border-[#FFE600]/20 p-6" data-testid="recommendation">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-[#FFE600] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[#FFE600] font-bold font-['Cabinet_Grotesk'] text-sm uppercase tracking-wider mb-1">{t.bm_top_rec || "Top Recommendation"}</p>
                    <p className="text-[var(--color-text)] text-sm leading-relaxed">{latestResult.top_recommendation}</p>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Change Alert */}
            {results?.results?.length >= 2 && (() => {
              const curr = results.results[0]?.overall_visibility || 0;
              const prev = results.results[1]?.overall_visibility || 0;
              const change = curr - prev;
              if (Math.abs(change) < 5) return null;
              const isUp = change > 0;
              return (
                <div className={`p-4 border ${isUp ? 'bg-emerald-900/10 border-emerald-900/30' : 'bg-red-900/10 border-purple-900/30'}`} data-testid="visibility-change-alert">
                  <div className="flex items-center gap-3">
                    {isUp ? <ArrowUp className="w-5 h-5 text-emerald-400" /> : <ArrowDown className="w-5 h-5 text-red-400" />}
                    <span className={`font-bold font-['Cabinet_Grotesk'] ${isUp ? 'text-emerald-400' : 'text-red-400'}`}>
                      {isUp ? '+' : ''}{change}%
                    </span>
                    <span className="text-[var(--color-text-muted)] text-sm">
                      {isUp
                        ? (t.bm_vis_up || "Visibility increased since last check")
                        : (t.bm_vis_down || "Visibility decreased since last check")
                      } ({prev}% → {curr}%)
                    </span>
                  </div>
                </div>
              );
            })()}

            {/* Queries */}
            {latestResult.queries?.length > 0 && (
              <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6" data-testid="query-results">
                <h3 className="text-sm font-bold text-[var(--color-text-muted)] uppercase tracking-wider mb-4 font-['JetBrains_Mono']">{t.bm_query_analysis || "Query Analysis"}</h3>
                <div className="space-y-3">
                  {latestResult.queries.map((q, i) => (
                    <div key={i} className="flex items-center justify-between py-3 border-b border-[var(--color-border)] last:border-0">
                      <p className="text-sm text-[var(--color-text)] flex-1">"{q.query}"</p>
                      <div className="flex gap-2 ml-4">
                        {["chatgpt", "perplexity", "gemini", "copilot"].map(eng => {
                          const citing = (q.engines_citing || []).includes(eng);
                          const meta = ENGINE_META[eng];
                          return (
                            <span key={eng} className={`w-7 h-7 flex items-center justify-center text-[10px] font-bold border ${
                              citing ? 'border-emerald-500/50 text-emerald-400' : 'border-[var(--color-border)] text-zinc-700'
                            }`} title={meta?.name}>
                              {meta?.name?.[0]}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Check timestamp */}
            <p className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono'] text-center">
              {t.bm_last_checked || "Last checked"}: {new Date(latestResult.checked_at).toLocaleString("sk-SK")}
            </p>
          </div>
        )}

        {/* Loading state */}
        {loading && !latestResult && selectedMonitor && (
          <div className="text-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#a855f7] mx-auto mb-4" />
            <p className="text-[var(--color-text-muted)]">{t.bm_loading || "Analyzing AI visibility..."}</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="py-8 px-6 bg-[var(--color-bg)] border-t border-[var(--color-border)] mt-12">
        <div className="max-w-7xl mx-auto">
          {finalCta}
        </div>
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#a855f7] flex items-center justify-center"><TrendingUp className="w-4 h-4 text-white" /></div>
            <span className="font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
          </div>
          <p className="text-zinc-700 text-xs font-['JetBrains_Mono']">© 2026 TopRankPulse</p>
        </div>
      </footer>
    </div>
  );
}
